import React from 'react';
import { SelectOption } from 'global';
import { useSelector } from 'react-redux';
import { useQuery } from '@apollo/client';
import { accountSelectors } from 'redux/accountReducer';
import { CircularProgress, Grid, Stack } from '@mui/material';
import { CardContainer } from 'components/shared/CardContainer/CardContainer';
import { externalDashboardsApi } from 'modules/integrations/externalDashboards';
import { portalConfigurationsSelectors } from 'redux/portalConfigurationReducer';
import { RenderWithFallback } from 'components/shared/Conditional/RenderWithFallback';
import { PortalConfiguration, PortalConfigurationType } from '@trnsact/trnsact-shared-types';

import { usePortalConfigurationChecks } from '../../hooks';
import { PortalConfigurationRenderProps } from '../../types';
import { ExternalDashboardPortal } from './ExternalDashboardPortal';
import { PortalConfigurationRolesSettingsKeys } from '../../../../types';
import { ExternalDashboardPortalConfig, ExternalDashboardPortalForm } from '../types';
import { getPortalConfigurationDefaultConfig } from '../../lib/getPortalConfigurationDefaultConfig';

export const ExternalDashboardPortalManagement = ({
  loading,
  isSuperAdminOrSingleAccount,
  vendorProfile,
  onSaveConfiguration,
}: PortalConfigurationRenderProps) => {
  const accountType = useSelector(accountSelectors.accountType);

  const { data, loading: dashboardListLoading } = useQuery(externalDashboardsApi.getDashboardList, {
    skip: !accountType,
    variables: {
      input: { accountType },
    },
  });

  const listOfAvailableDashboardForAccount: SelectOption[] = (data?.externalDashboards?.result?.list ?? []).map(
    (item: any) => ({
      label: item.name,
      value: item.id,
    })
  );

  const { checkPortalConfigEntryForCurrentUserRole } = usePortalConfigurationChecks();

  const isHiddenByUserRole = checkPortalConfigEntryForCurrentUserRole(
    PortalConfigurationType.PortalSetup,
    PortalConfigurationRolesSettingsKeys.InternalCreditTiersManagementHidden
  );

  const { portalConfig, jsonConfig } = useSelector((state: any) => {
    const defaultConfig = getPortalConfigurationDefaultConfig(
      vendorProfile.id,
      PortalConfigurationType.ExternalDashboardAccess
    );

    const portalConfig =
      portalConfigurationsSelectors.configDataByType(state, PortalConfigurationType.ExternalDashboardAccess) ??
      defaultConfig;
    const jsonConfig = portalConfig?.jsonDefinition?.config ?? defaultConfig.jsonDefinition.config;

    return { portalConfig, jsonConfig };
  });

  const handleSave = async (formData: ExternalDashboardPortalForm) => {
    const config = formData.dashboards.reduce<ExternalDashboardPortalConfig>(
      (acc, { dashboardId, hiddenFor }) => {
        acc.dashboardHiddenFor[dashboardId] = hiddenFor;
        return acc;
      },
      { dashboardHiddenFor: {} }
    );

    const configToSave: PortalConfiguration = { ...portalConfig, jsonDefinition: { config } };

    await onSaveConfiguration(configToSave);
  };

  if (!isSuperAdminOrSingleAccount && isHiddenByUserRole) return null;

  return (
    <Grid item sm={12} md={6}>
      <CardContainer title="External Dashboards Management">
        <RenderWithFallback
          isLoading={!portalConfig || dashboardListLoading}
          fallBack={
            <Stack alignItems="center" justifyContent="center" height="100%">
              <CircularProgress />
            </Stack>
          }
        >
          <ExternalDashboardPortal
            isLoading={loading}
            onConfirm={handleSave}
            config={jsonConfig?.dashboardHiddenFor ?? {}}
            dashboardOptions={listOfAvailableDashboardForAccount}
          />
        </RenderWithFallback>
      </CardContainer>
    </Grid>
  );
};
